// src/components/setup/steps/EmailVerification.jsx
import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, AlertCircle, RefreshCw, ChevronDown } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { verifyEmail, resendVerificationEmail, changeEmail } from '../../../services/api/auth';
import styles from './StepComponents.module.css';

const EmailVerification = ({ user, onNext, isSubmitting }) => {
  const { refreshUser } = useAuth();
  const { t } = useLocalization();
  const { success, error } = useAlert();

  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verificationError, setVerificationError] = useState('');

  // Change email state
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newEmailError, setNewEmailError] = useState('');
  const [isChangingEmail, setIsChangingEmail] = useState(false);

  // Check if email is already verified
  useEffect(() => {
    if (user?.emailVerified) {
      setTimeout(onNext, 1000);
    }
  }, [user?.emailVerified, onNext]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      setVerificationError(t('setup.enterVerificationCode'));
      return;
    }
    setIsVerifying(true);
    setVerificationError('');
    try {
      await verifyEmail({ code: verificationCode });
      await refreshUser();
      success(t('setup.emailVerifiedSuccess'));
      onNext();
    } catch (err) {
      setVerificationError(err.response?.data?.message || t('setup.verificationError'));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setVerificationError('');
    try {
      await resendVerificationEmail();
      success(t('setup.verificationCodeSent'));
      setResendCooldown(60);
    } catch (err) {
      error(err.response?.data?.message || t('setup.resendError'));
    } finally {
      setIsResending(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !/\S+@\S+\.\S+/.test(newEmail)) {
      setNewEmailError(t('setup.invalidEmail'));
      return;
    }
    setIsChangingEmail(true);
    setNewEmailError('');
    try {
      await changeEmail({ newEmail });
      await refreshUser();
      success(t('setup.emailChanged'));
      setShowChangeEmail(false);
      setNewEmail('');
      setResendCooldown(60);
    } catch (err) {
      setNewEmailError(err.response?.data?.message || t('setup.emailChangeError'));
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
    if (verificationError) setVerificationError('');
  };

  // Already verified
  if (user?.emailVerified) {
    return (
      <div className={styles.stepContainer}>
        <div className={styles.stepHeader}>
          <CheckCircle className={styles.successIcon} size={48} />
          <h2>{t('setup.emailAlreadyVerified')}</h2>
          <p>{t('setup.emailVerifiedMessage')}</p>
        </div>
        <div className={styles.verificationSuccess}>
          <div className={styles.successCard}>
            <Mail size={24} />
            <span>{user.email}</span>
            <CheckCircle className={styles.verifiedBadge} size={20} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepHeader}>
        <Mail className={styles.stepIcon} size={48} />
        <h2>{t('setup.verifyEmailTitle')}</h2>
        <p>{t('setup.verifyEmailDescription', { email: user?.email })}</p>
      </div>

      <form onSubmit={handleVerifyEmail} className={styles.stepForm}>
        <div className={styles.formGroup}>
          <label htmlFor="verificationCode">
            {t('setup.verificationCode')}
          </label>
          <input
            type="text"
            id="verificationCode"
            value={verificationCode}
            onChange={handleCodeChange}
            placeholder="123456"
            className={`${styles.codeInput} ${verificationError ? styles.error : ''}`}
            maxLength="6"
            autoComplete="one-time-code"
            autoFocus
          />
          {verificationError && (
            <span className={styles.errorText}>{verificationError}</span>
          )}
          <span className={styles.formHint}>
            {t('setup.verificationCodeHint')}
          </span>
        </div>

        <div className={styles.verificationActions}>
          <button
            type="submit"
            disabled={!verificationCode || isVerifying || isSubmitting}
            className={styles.verifyButton}
          >
            {isVerifying ? (
              <>
                <div className={styles.loadingSpinner}></div>
                {t('setup.verifying')}
              </>
            ) : (
              <>
                <CheckCircle size={20} />
                {t('setup.verifyEmail')}
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handleResendCode}
            disabled={isResending || resendCooldown > 0}
            className={styles.resendButton}
          >
            {isResending ? (
              <>
                <div className={styles.loadingSpinner}></div>
                {t('setup.resending')}
              </>
            ) : resendCooldown > 0 ? (
              <>
                <RefreshCw size={16} />
                {t('setup.resendIn', { seconds: resendCooldown })}
              </>
            ) : (
              <>
                <RefreshCw size={16} />
                {t('setup.resendCode')}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Change email section */}
      <div className={styles.changeEmailSection}>
        <button
          type="button"
          className={styles.changeEmailToggle}
          onClick={() => setShowChangeEmail(!showChangeEmail)}
        >
          <Mail size={16} />
          {t('setup.changeEmail')}
          <ChevronDown size={14} style={{ transform: showChangeEmail ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </button>

        {showChangeEmail && (
          <form onSubmit={handleChangeEmail} className={styles.changeEmailForm}>
            <p className={styles.changeEmailDesc}>{t('setup.changeEmailDescription')}</p>
            <div className={styles.formGroup}>
              <label htmlFor="newEmail">{t('setup.newEmailLabel')}</label>
              <input
                type="email"
                id="newEmail"
                value={newEmail}
                onChange={(e) => { setNewEmail(e.target.value); if (newEmailError) setNewEmailError(''); }}
                placeholder={t('setup.newEmailPlaceholder')}
                className={newEmailError ? styles.error : ''}
                autoComplete="email"
              />
              {newEmailError && <span className={styles.errorText}>{newEmailError}</span>}
            </div>
            <button
              type="submit"
              disabled={isChangingEmail || !newEmail}
              className={styles.secondaryButton}
            >
              {isChangingEmail ? (
                <>
                  <div className={styles.loadingSpinner}></div>
                  {t('setup.changingEmail')}
                </>
              ) : (
                t('setup.sendNewCode')
              )}
            </button>
          </form>
        )}
      </div>

      <div className={styles.helpSection}>
        <h4>{t('setup.needHelp')}</h4>
        <ul>
          <li>{t('setup.checkSpamFolder')}</li>
          <li>{t('setup.checkEmailAddress')}</li>
          <li>{t('setup.contactSupportIf')}</li>
        </ul>
      </div>
    </div>
  );
};

export default EmailVerification;
